import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Flame } from "lucide-react";

export default function BilanForm({ onDone, subscribed }) {
  const [form, setForm] = useState({ weight_kg: "", energy_level: 3, difficulty: "adapte", feedback: "" });
  const [busy, setBusy] = useState(false);
  const [sessionsDone, setSessionsDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!subscribed) return;
    apiClient.get("/bilan/prefill").then(({ data }) => setSessionsDone(data.sessions_done)).catch(() => setSessionsDone(0));
  }, [subscribed]);

  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.post("/bilan", {
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
        energy_level: parseInt(form.energy_level),
        difficulty: form.difficulty,
        feedback: form.feedback,
      });
      toast.success("Génération de ton programme ajusté lancée… 🌱");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du bilan.");
    } finally { setBusy(false); }
  };

  if (!subscribed) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#141C17] p-8 text-center">
        <h3 className="font-display text-lg font-bold mb-2">Bilan des 4 semaines</h3>
        <p className="text-sm text-white/50 mb-5">Le bilan est disponible avec un abonnement actif.</p>
        <a href="/pricing" className="inline-block bg-lime-400 text-black font-bold px-6 py-2.5 rounded-xl">Voir les formules</a>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
      <div className="flex items-center gap-2 mb-1">
        <RefreshCw className="h-5 w-5 text-lime-400" />
        <h3 className="font-display font-bold">Bilan des 4 semaines</h3>
      </div>
      <p className="text-xs text-white/40 mb-5">Fais le point pour recevoir un programme recalculé et ajusté.</p>

      <div className="space-y-3">
        {/* Auto pre-filled sessions done */}
        <div data-testid="bilan-sessions-done" className="flex items-center gap-3 rounded-xl bg-lime-400/[0.06] border border-lime-400/20 px-4 py-3">
          <Flame className="h-5 w-5 text-lime-400 shrink-0" />
          <div>
            <p className="text-sm font-semibold">
              {sessionsDone === null ? "Chargement…" : `${sessionsDone} séance${sessionsDone > 1 ? "s" : ""} réalisée${sessionsDone > 1 ? "s" : ""}`}
            </p>
            <p className="text-xs text-white/50">Rempli automatiquement depuis ton suivi.</p>
          </div>
        </div>

        <input data-testid="bilan-weight-input" type="number" placeholder="Nouveau poids (kg, optionnel)" value={form.weight_kg}
          onChange={(e) => set("weight_kg", e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />

        <div>
          <label className="text-xs text-white/50 ml-1">Niveau d'énergie ce mois : {form.energy_level}/5</label>
          <input type="range" min="1" max="5" value={form.energy_level} onChange={(e) => set("energy_level", e.target.value)}
            className="w-full accent-lime-400 mt-2" />
        </div>

        <div>
          <label className="text-xs text-white/50 ml-1">Difficulté ressentie</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {[["trop_facile", "Trop facile"], ["adapte", "Adapté"], ["trop_dur", "Trop dur"]].map(([k, l]) => (
              <button key={k} data-testid={`bilan-difficulty-${k}`} onClick={() => set("difficulty", k)}
                className={`rounded-xl py-2.5 text-sm font-semibold border transition-all ${form.difficulty === k ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 text-white/60"}`}>{l}</button>
            ))}
          </div>
        </div>

        <textarea data-testid="bilan-feedback-textarea" placeholder="Un ressenti à partager ? (optionnel)" value={form.feedback}
          onChange={(e) => set("feedback", e.target.value)} rows={3}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50 resize-none" />

        <button data-testid="bilan-monthly-submit-button" onClick={submit} disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-lime-400 text-black font-extrabold py-3 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all disabled:opacity-60">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Génération…</> : "Générer mon programme ajusté"}
        </button>
      </div>
    </div>
  );
}
